import { supabase } from './supabase';

export type ConnectionStatus = 'connected' | 'reconnecting' | 'disconnected' | 'degraded';

export interface ConnectionState {
  status: ConnectionStatus;
  lastSuccessfulConnection: Date | null;
  lastFailedConnection: Date | null;
  consecutiveFailures: number;
  isPageVisible: boolean;
  retryAttempts: number;
  networkOnline: boolean;
}

type ConnectionListener = (state: ConnectionState) => void;

class ConnectionManager {
  private state: ConnectionState = {
    status: 'connected',
    lastSuccessfulConnection: new Date(),
    lastFailedConnection: null,
    consecutiveFailures: 0,
    isPageVisible: true,
    retryAttempts: 0,
    networkOnline: navigator.onLine,
  };

  private listeners: ConnectionListener[] = [];
  private healthCheckInterval: NodeJS.Timeout | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private visibilityChangeHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;
  private lastHealthCheck: number = 0;
  private lastFocusCheck: number = 0;
  private focusDebounceTimer: NodeJS.Timeout | null = null;

  private readonly HEALTH_CHECK_INTERVAL = 60000;
  private readonly MAX_CONSECUTIVE_FAILURES = 3;
  private readonly RECONNECT_DELAYS = [1000, 2000, 5000, 10000, 30000];
  private readonly MIN_HEALTH_CHECK_GAP_MS = 10000;
  private readonly FOCUS_DEBOUNCE_MS = 2000;

  constructor() {
    this.setupEventListeners();
    this.startHealthCheck();
  }

  private setupEventListeners() {
    this.visibilityChangeHandler = () => this.handleVisibilityChange();
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);

    this.onlineHandler = () => this.handleOnlineStatus(true);
    this.offlineHandler = () => this.handleOnlineStatus(false);
    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);

    window.addEventListener('focus', () => this.handleWindowFocus());
    window.addEventListener('blur', () => this.handleWindowBlur());
  }

  private handleVisibilityChange() {
    const isVisible = !document.hidden;
    this.updateState({ isPageVisible: isVisible });

    if (isVisible && this.state.status !== 'connected') {
      const now = Date.now();
      if (now - this.lastHealthCheck > this.MIN_HEALTH_CHECK_GAP_MS) {
        console.log('[ConnectionManager] Page became visible, checking connection health...');
        this.checkConnectionHealth();
      } else {
        console.log('[ConnectionManager] Page became visible, but skipping health check (too recent)');
      }
    }

    if (isVisible) {
      this.startHealthCheck();
    } else {
      // Don't stop health checks completely, just reduce frequency
      console.log('[ConnectionManager] Page hidden, reducing health check frequency');
    }
  }

  private handleWindowFocus() {
    if (this.focusDebounceTimer) {
      clearTimeout(this.focusDebounceTimer);
    }

    this.focusDebounceTimer = setTimeout(() => {
      const now = Date.now();
      if (now - this.lastFocusCheck < 3000) {
        return;
      }
      this.lastFocusCheck = now;

      console.log('[ConnectionManager] Window gained focus');
      if (this.state.status !== 'connected' && now - this.lastHealthCheck > this.MIN_HEALTH_CHECK_GAP_MS) {
        this.checkConnectionHealth();
      }
    }, this.FOCUS_DEBOUNCE_MS);
  }

  private handleWindowBlur() {
    console.log('[ConnectionManager] Window lost focus');
  }

  private handleOnlineStatus(online: boolean) {
    console.log(`[ConnectionManager] Network status: ${online ? 'online' : 'offline'}`);
    this.updateState({ networkOnline: online });

    if (online && this.state.status !== 'connected') {
      console.log('[ConnectionManager] Network back online, attempting reconnection...');
      this.attemptReconnection();
    } else if (!online) {
      this.updateState({ status: 'disconnected' });
    }
  }

  private startHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }

    this.healthCheckInterval = setInterval(() => {
      if (!document.hidden && this.state.networkOnline) {
        this.checkConnectionHealth();
      }
    }, this.HEALTH_CHECK_INTERVAL);
  }

  private stopHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  private async checkConnectionHealth(): Promise<boolean> {
    const now = Date.now();

    // Prevent too frequent health checks
    if (now - this.lastHealthCheck < this.MIN_HEALTH_CHECK_GAP_MS) {
      console.log('[ConnectionManager] Skipping health check (too recent)');
      return this.state.status === 'connected';
    }

    this.lastHealthCheck = now;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const { error } = await supabase
        .from('products')
        .select('id')
        .limit(1)
        .abortSignal(controller.signal as AbortSignal);

      clearTimeout(timeoutId);

      if (error) {
        console.error('[ConnectionManager] Health check failed:', error.message);
        this.handleConnectionFailure();
        return false;
      }

      this.handleConnectionSuccess();
      return true;
    } catch (error: any) {
      console.error('[ConnectionManager] Health check error:', error.message);
      this.handleConnectionFailure();
      return false;
    }
  }

  private handleConnectionSuccess() {
    const wasDisconnected = this.state.status !== 'connected';

    this.updateState({
      status: 'connected',
      lastSuccessfulConnection: new Date(),
      consecutiveFailures: 0,
      retryAttempts: 0,
    });

    if (wasDisconnected) {
      console.log('[ConnectionManager] Connection restored successfully');
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private handleConnectionFailure() {
    const newFailureCount = this.state.consecutiveFailures + 1;

    let newStatus: ConnectionStatus = this.state.status;
    if (newFailureCount >= this.MAX_CONSECUTIVE_FAILURES) {
      newStatus = 'disconnected';
    } else if (newFailureCount > 1) {
      newStatus = 'degraded';
    }

    this.updateState({
      status: newStatus,
      lastFailedConnection: new Date(),
      consecutiveFailures: newFailureCount,
    });

    if (newStatus === 'disconnected' || newStatus === 'degraded') {
      this.scheduleReconnection();
    }
  }

  private scheduleReconnection() {
    if (this.reconnectTimeout) {
      return;
    }

    const delayIndex = Math.min(
      this.state.retryAttempts,
      this.RECONNECT_DELAYS.length - 1
    );
    const delay = this.RECONNECT_DELAYS[delayIndex];

    console.log(`[ConnectionManager] Scheduling reconnection attempt in ${delay}ms...`);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.attemptReconnection();
    }, delay);
  }

  private async attemptReconnection() {
    if (this.state.status === 'reconnecting') {
      return;
    }

    this.updateState({
      status: 'reconnecting',
      retryAttempts: this.state.retryAttempts + 1,
    });

    console.log(`[ConnectionManager] Attempting reconnection (attempt ${this.state.retryAttempts})...`);

    const success = await this.checkConnectionHealth();

    if (!success && this.state.networkOnline) {
      this.scheduleReconnection();
    }
  }

  private updateState(updates: Partial<ConnectionState>) {
    const oldStatus = this.state.status;
    this.state = { ...this.state, ...updates };

    if (oldStatus !== this.state.status) {
      console.log(`[ConnectionManager] Status changed: ${oldStatus} -> ${this.state.status}`);
    }

    this.notifyListeners();
  }

  private notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener(this.state);
      } catch (error) {
        console.error('[ConnectionManager] Error in listener:', error);
      }
    });
  }

  public getState(): ConnectionState {
    return { ...this.state };
  }

  public subscribe(listener: ConnectionListener): () => void {
    this.listeners.push(listener);
    listener(this.state);

    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  public forceHealthCheck(): Promise<boolean> {
    return this.checkConnectionHealth();
  }

  public destroy() {
    this.stopHealthCheck();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.focusDebounceTimer) {
      clearTimeout(this.focusDebounceTimer);
      this.focusDebounceTimer = null;
    }

    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
    }

    if (this.offlineHandler) {
      window.removeEventListener('offline', this.offlineHandler);
    }

    this.listeners = [];
  }
}

export const connectionManager = new ConnectionManager();
